import { Suspense, lazy } from 'react';
import type { NavSection } from '../types';
import { ViewSkeleton } from './Common/SuspenseFallback';
import { Dashboard } from './Dashboard/Dashboard';

// Lazy load view components for better performance
const AgentsView = lazy(() => 
  import('./Views/AgentsView').then(m => ({ default: m.AgentsView }))
);
const PlanView = lazy(() => 
  import('./Views/PlanView').then(m => ({ default: m.PlanView }))
);
const BuildView = lazy(() => 
  import('./Views/BuildView').then(m => ({ default: m.BuildView }))
);
const ExecutionView = lazy(() => 
  import('./Views/ExecutionView').then(m => ({ default: m.ExecutionView }))
);
const CodeReviewView = lazy(() => 
  import('./Views/CodeReviewView').then(m => ({ default: m.CodeReviewView }))
);
const ReportsView = lazy(() => 
  import('./Views/ReportsView').then(m => ({ default: m.ReportsView }))
);
const UsageView = lazy(() => 
  import('./Views/UsageView').then(m => ({ default: m.UsageView }))
);
const PricingView = lazy(() => 
  import('./Views/PricingView').then(m => ({ default: m.PricingView }))
);
const BillingView = lazy(() => 
  import('./Views/BillingView').then(m => ({ default: m.BillingView }))
);
const IntegrationsView = lazy(() => 
  import('./Views/IntegrationsView').then(m => ({ default: m.IntegrationsView }))
);
const OrganizationSettingsView = lazy(() => 
  import('./Views/OrganizationSettingsView').then(m => ({ default: m.OrganizationSettingsView }))
);
const UserManagementView = lazy(() => 
  import('./Views/UserManagementView').then(m => ({ default: m.UserManagementView }))
);
const AdminView = lazy(() => 
  import('./Views/AdminView').then(m => ({ default: m.AdminView }))
);
const EnhancedJiraView = lazy(() => 
  import('./Views/EnhancedJiraView').then(m => ({ default: m.EnhancedJiraView }))
);
const WelcomeView = lazy(() => 
  import('./Views/WelcomeView').then(m => ({ default: m.WelcomeView }))
);

interface ViewRouterProps {
  activeSection: NavSection;
  projectId?: string | null;
  // Props that might be needed by specific views
  onSelectProject?: (projectId: string) => void;
  onDeleteProject?: (projectId: string) => void;
  onProjectCreated?: (projectId: string) => void;
}

export function ViewRouter({ 
  activeSection, 
  projectId,
  onSelectProject,
  onDeleteProject,
  onProjectCreated,
}: ViewRouterProps) {
  // Render appropriate view based on active section
  switch (activeSection) {
    case 'welcome':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <Dashboard
            onSelectProject={onSelectProject || (() => {})}
            onDeleteProject={onDeleteProject || (() => {})}
            onProjectCreated={onProjectCreated}
          />
        </Suspense>
      );

    case 'agents':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <AgentsView projectId={projectId} />
        </Suspense>
      );

    case 'blueprint':
      // Blueprint view - redirect to plan mode if projectId exists, otherwise show empty state
      if (projectId) {
        // This should redirect to /plan/{projectId} - handled by MainApp
        return (
          <Suspense fallback={<ViewSkeleton />}>
            <div className="p-6">
              <p className="text-text-secondary">Redirecting to plan mode...</p>
            </div>
          </Suspense>
        );
      }
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <div className="p-6">
            <p className="text-text-secondary">Please select a project to view its blueprint.</p>
          </div>
        </Suspense>
      );

    case 'ide':
      // IDE view - show project selector if no projectId, otherwise redirect handled by MainApp
      if (projectId) {
        // This should redirect to /ide/{projectId} - handled by MainApp
        return (
          <Suspense fallback={<ViewSkeleton />}>
            <div className="p-6">
              <p className="text-text-secondary">Redirecting to IDE...</p>
            </div>
          </Suspense>
        );
      }
      // Show project selector when IDE is accessed without a project
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <Dashboard
            onSelectProject={onSelectProject || (() => {})}
            onDeleteProject={onDeleteProject || (() => {})}
            onProjectCreated={onProjectCreated}
          />
        </Suspense>
      );

    case 'jira':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <EnhancedJiraView projectId={projectId} />
        </Suspense>
      );

    case 'plan':
      // Plan view - this is a simplified view, full plan mode is at /plan/{projectId}
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <PlanView 
            ticket={null}
            onSaveSpec={() => {}}
            onGenerateWorkPlan={() => {}}
          />
        </Suspense>
      );

    case 'build':
      // Build view requires ticket and phase data - simplified for now
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <BuildView
            sdlcPhases={[]}
            phaseExecutions={[]}
            loadingPhases={false}
            currentTicket={null}
            onStartPhase={() => {}}
            onInitializePhases={() => {}}
          />
        </Suspense>
      );

    case 'execution':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <ExecutionView projectId={projectId} currentTicket={null} />
        </Suspense>
      );

    case 'review':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <CodeReviewView projectId={projectId} />
        </Suspense>
      );

    case 'reports':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <ReportsView projectId={projectId} />
        </Suspense>
      );

    case 'usage':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <UsageView />
        </Suspense>
      );

    case 'pricing':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <PricingView />
        </Suspense>
      );

    case 'billing':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <BillingView />
        </Suspense>
      );

    case 'integrations':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <IntegrationsView />
        </Suspense>
      );

    case 'organization':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <OrganizationSettingsView />
        </Suspense>
      );

    case 'users':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <UserManagementView />
        </Suspense>
      );

    case 'admin':
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <AdminView />
        </Suspense>
      );

    default:
      // Fallback to dashboard
      return (
        <Suspense fallback={<ViewSkeleton />}>
          <Dashboard
            onSelectProject={onSelectProject || (() => {})}
            onDeleteProject={onDeleteProject || (() => {})}
            onProjectCreated={onProjectCreated}
          />
        </Suspense>
      );
  }
}

